import React, { useCallback } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import LoginPage from 'pages/LoginPage/LoginPage';
import PrivateRoute from 'routes/PrivateRoute';
import RegisterPage from 'pages/RegisterPage/RegisterPage';
import getRedirectPath from 'modules/get-redirect-path/get-redirect-path';
import UploadSATPage from 'pages/UploadSATPage/UploadSATPage';
import {
  canPrintLabels,
  canReprintLabels,
  canRetryShipments,
  isAdmin,
  isB2BPrinter,
  isB2BUploader,
  isBulkdrop,
  isDSVPrinter,
  isDSVRetry,
  isDSVStock,
  isLolina,
  isOllas,
  isSAT,
  isSATPrinter,
  isSATUploader,
  isScanPack,
  isSFPStock,
  isSorterRetry,
  isStaff,
  isStock,
} from 'modules/user-has-permission/user-has-permission';
import UploadOllasPage from 'pages/UploadOllasPage/UploadOllasPage';
import OllasPrinterPage from 'pages/OllasPrinterPage/OllasPrinterPage';
import SATPrinterPage from 'pages/SATPrinterPage/SATPrinterPage';
import PreparationOrderPage from 'pages/PreparationOrderPage/PreparationOrderPage';
import PreparationOrdersPage from 'pages/PreparationOrdersPage/PreparationOrdersPage';
import UploadB2BPage from 'pages/UploadB2BPage/UploadB2BPage';
import ZebraProvider from 'context/ZebraProvider';
import MaintenanceModePage from 'pages/MaintenanceModePage/MaintenanceModePage';
import MePage from 'pages/MePage/MePage';
import RetryShipmentsPage from 'pages/RetryShipmentsPage/RetryShipmentsPage';
import RetryShipmentPage from 'pages/RetryShipmentPage/RetryShipmentPage';
import LoginCallbackPage from 'pages/LoginCallbackPage/LoginCallbackPage';
import LogoutPage from 'pages/LogoutPage/LogoutPage';
import { ORIGINS } from 'constants/constants';
import ManifestsPage from 'pages/ManifestsPage/ManifestsPage';
import ConfigurationPage from 'pages/ConfigurationPage/ConfigurationPage';
import WarehouseManagementPage from 'pages/WarehouseManagementPage/WarehouseManagementPage';
import MarketplacePendingOrdersPage from 'pages/MarketplacePendingOrdersPage/MarketplacePendingOrdersPage';
import ForgotPasswordPage from 'pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from 'pages/ResetPasswordPage/ResetPasswordPage';
import PublicRoute from 'routes/PublicRoute';
import LabelsPage from 'pages/LabelsPage/LabelsPage';

export default function AppRouter() {
  const getRedirect = useCallback(getRedirectPath, []);

  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true')
    return <MaintenanceModePage />;

  return (
    <Router>
      <Switch>
        <Route
          path="/"
          exact
          component={() => <Redirect to={getRedirect()} />}
        />
        <Route path="/login" exact component={LoginPage} />
        <Route path="/login-callback" exact component={LoginCallbackPage} />
        <Route path="/logout" exact component={LogoutPage} />
        <PublicRoute path="/register" exact component={RegisterPage} />
        <PublicRoute
          path="/forgot-password"
          exact
          component={ForgotPasswordPage}
        />
        <PublicRoute
          path="/reset-password"
          exact
          component={ResetPasswordPage}
        />
        <PrivateRoute
          path="/ollas-upload"
          exact
          component={() =>
            isOllas() ? <UploadOllasPage /> : <Redirect to={getRedirect()} />
          }
        />
        <PrivateRoute
          path="/ollas-printer"
          exact
          component={() =>
            isOllas() ? (
              <ZebraProvider>
                <OllasPrinterPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/sat-upload"
          exact
          component={() =>
            isSATUploader() ? (
              <UploadSATPage />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/sat-printer"
          exact
          component={() =>
            isSATPrinter() ? (
              <ZebraProvider>
                <SATPrinterPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/b2b-upload"
          exact
          component={() =>
            isB2BUploader() ? (
              <UploadB2BPage />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/preparation-orders"
          exact
          component={() =>
            isB2BPrinter() || isDSVPrinter() || isDSVStock() || isSFPStock() ? (
              <ZebraProvider autoConnect={!isStock()}>
                <PreparationOrdersPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/preparation-orders/:reference"
          exact
          component={() =>
            isB2BPrinter() || isDSVPrinter() || isDSVStock() || isSFPStock() ? (
              <ZebraProvider autoConnect={!isStock()}>
                <PreparationOrderPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/manifests"
          exact
          component={() =>
            isB2BPrinter() ||
            isDSVPrinter() ||
            isDSVStock() ||
            (isSAT() && canPrintLabels()) ? (
              <ZebraProvider>
                <ManifestsPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/reprint-labels"
          exact
          component={() =>
            canReprintLabels() ? (
              <ZebraProvider>
                <LabelsPage />
              </ZebraProvider>
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-shipments"
          exact
          component={() =>
            canRetryShipments() ? (
              <RetryShipmentsPage />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-shipments/:reference"
          exact
          component={() =>
            canRetryShipments() ? (
              <RetryShipmentPage />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-lolina-shipments"
          exact
          component={() =>
            isLolina() ? (
              <RetryShipmentsPage origin={ORIGINS.LOLINA.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-lolina-shipments/:reference"
          exact
          component={() =>
            isLolina() ? (
              <RetryShipmentPage origin={ORIGINS.LOLINA.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-sorter-shipments"
          exact
          component={() =>
            isSorterRetry() ? (
              <RetryShipmentsPage origin={ORIGINS.SORTER.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-sorter-shipments/:reference"
          exact
          component={() =>
            isSorterRetry() ? (
              <RetryShipmentPage origin={ORIGINS.SORTER.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-bulkdrop-shipments"
          exact
          component={() =>
            isBulkdrop() ? (
              <RetryShipmentsPage origin={ORIGINS.BULKDROP.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-bulkdrop-shipments/:reference"
          exact
          component={() =>
            isBulkdrop() ? (
              <RetryShipmentPage origin={ORIGINS.BULKDROP.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-scanpack-shipments"
          exact
          component={() =>
            isScanPack() ? (
              <RetryShipmentsPage origin={ORIGINS.SCANPACK.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/retry-scanpack-shipments/:reference"
          exact
          component={() =>
            isScanPack() ? (
              <RetryShipmentPage origin={ORIGINS.SCANPACK.slug} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/configuration"
          component={(props) =>
            isAdmin() ? (
              <ConfigurationPage {...props} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/warehouse-management"
          component={(props) =>
            isAdmin() || isStaff() ? (
              <WarehouseManagementPage {...props} />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute
          path="/dsv/pending-orders"
          exact
          component={() =>
            isDSVRetry() ? (
              <MarketplacePendingOrdersPage />
            ) : (
              <Redirect to={getRedirect()} />
            )
          }
        />
        <PrivateRoute path="/me" component={MePage} />
        <Route component={() => <div>not found</div>} />
      </Switch>
    </Router>
  );
}
